import * as React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { DataTable } from "@/components/ui/datatable";
import type { ColumnDef } from "@tanstack/react-table";
import { Finding, CorrectiveAction } from "@/app/pages/auditcentre";
import { useTenancy } from "@/contexts/TenancyContext";
import { CorrectiveActionDialog } from "./CorrectiveActionDialog";

interface CorrectiveActionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  finding: Finding | null;
  onActionCreated: (action: CorrectiveAction) => void;
  onActionUpdated: (actionId: string, updates: Partial<CorrectiveAction>) => void;
  onActionDeleted: (actionId: string) => void;
}

export function CorrectiveActionsDialog({
  open,
  onOpenChange,
  finding,
  onActionCreated,
  onActionUpdated,
  onActionDeleted,
}: CorrectiveActionsDialogProps) {
  const { userIdToName } = useTenancy();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = React.useState(false);
  const [editingAction, setEditingAction] = React.useState<CorrectiveAction | null>(null);

  const handleActionCreated = async (action: CorrectiveAction) => {
    try {
      // The parent's onActionCreated will handle the actual creation in Firestore
      // We just need to pass the action data through
      return await onActionCreated({
        ...action,
        // Ensure required fields are set
        status: 'Open' as const,
        owner: action.owner || 'Unassigned',
        summary: action.summary || 'No summary provided',
      });
    } catch (error) {
      console.error('Error handling action creation:', error);
      throw error;
    }
  };

  const handleActionUpdated = async (actionId: string, updates: Partial<CorrectiveAction>) => {
    try {
      // The parent's onActionUpdated will handle the actual update in Firestore
      await onActionUpdated(actionId, updates);
      
      // The parent will trigger a reload of the corrective actions
      // So we don't need to update local state here
    } catch (error) {
      console.error('Error updating corrective action:', error);
      throw error;
    }
  };

  if (!finding) return null;

  const columns: ColumnDef<CorrectiveAction>[] = [
    {
      accessorKey: "summary",
      header: "Summary",
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue() as string;
        return (
          <span className={`px-2 py-1 rounded-full text-xs ${
            status === "Open" 
              ? "bg-yellow-100 text-yellow-800" 
              : "bg-green-100 text-green-800"
          }`}>
            {status}
          </span>
        );
      },
    },
    {
      accessorKey: "dueDate",
      header: "Due Date",
      cell: (info) => {
        const dueDate = info.getValue();
        if (!dueDate) return <span className="text-muted-foreground">No due date</span>;
        
        // Handle different date formats
        let date: Date;
        if (typeof dueDate === 'string') {
          date = new Date(dueDate);
        } else if (dueDate instanceof Date) {
          date = dueDate;
        } else {
          // Handle FirebaseTimestamp
          try {
            date = dueDate.toDate();
          } catch (e) {
            return <span className="text-muted-foreground">Invalid date</span>;
          }
        }
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return <span className="text-muted-foreground">Invalid date</span>;
        }
        
        return <span>{date.toLocaleDateString()}</span>;
      },
    },
    {
      accessorKey: "owner",
      header: "Owner",
      cell: (info) => <span>{userIdToName[info.getValue()] || info.getValue()}</span>,
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const action = row.original;
        return (
          <div className="flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEditingAction(action)}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-800"
              onClick={() => onActionDeleted(action.id)}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-6xl min-h-[600px] max-w-6xl flex bg-card">
          <div className="w-1/2 pr-4 border-r">
            <DialogHeader>
              <DialogTitle>Finding: {finding.summary}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 mt-4">
              <p><strong>Type:</strong> {finding.type}</p>
              <p><strong>Status:</strong> {finding.status}</p>
              <p><strong>Owner:</strong> {userIdToName[finding.owner] || finding.owner}</p>

            </div>
          </div>
          
          <div className="w-1/2 pl-4">
            <div className="flex justify-between items-center mb-4">
              <DialogTitle>Corrective Actions</DialogTitle>
              <Button
                size="sm"
                onClick={() => setIsCreateDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Action
              </Button>
            </div>
            
            <div className="mt-6">
              <DataTable
                columns={columns}
                data={finding.correctiveActions || []}
                pageSize={10}
                selectable={false}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CorrectiveActionDialog
        open={isCreateDialogOpen || !!editingAction}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreateDialogOpen(false);
            setEditingAction(null);
          }
        }}
        action={editingAction}
        findings={finding ? [finding] : []}
        onSave={async (data) => {
          try {
            if (editingAction) {
              // For updates, ensure we don't include undefined values
              const updateData = { ...data };
              Object.keys(updateData).forEach(key => 
                updateData[key] === undefined && delete updateData[key]
              );
              
              await handleActionUpdated(editingAction.id, updateData);
            } else if (finding) {
              // For new actions, include the findingId and auditId
              const newAction = {
                ...data,
                findingId: finding.id,
                auditId: finding.auditId,
              };
              
              // This will call the parent's onActionCreated which handles Firestore creation
              await handleActionCreated(newAction as CorrectiveAction);
            }
          } catch (error) {
            console.error('Error saving corrective action:', error);
            // Re-throw to show error in the UI
            throw error;
          } finally {
            setIsCreateDialogOpen(false);
            setEditingAction(null);
          }
        }}
      />
    </>
  );
}
